import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputAdornment,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Controller, useFormContext, type FieldError } from 'react-hook-form';
import type { ExternalItemFormSchema } from './schema';
import { MoneyInput } from '@/components/shared/money-input';
import { PriceTypes } from '@/models/catalog/course';
import { ResourceTypes } from '@/models/resources';

const getErrorMessage = (error: FieldError | undefined): string | undefined =>
  error?.message;

const labels = {
  [ResourceTypes.EventOrLecture]: 'evento ou palestra',
  [ResourceTypes.Book]: 'livro',
  [ResourceTypes.Course]: 'curso',
  [ResourceTypes.Others]: 'item',
  [ResourceTypes.Podcast]: '',
  [ResourceTypes.Video]: '',
  [ResourceTypes.Document]: '',
};

export const LearningAidForm = ({ type }: { type: ResourceTypes }) => {
  const {
    control,
    formState: { errors },
    watch,
  } = useFormContext<ExternalItemFormSchema>();

  const saleType = watch('sale_type');

  return (
    <>
      <FormControl error={!!errors.name}>
        <Stack spacing={1}>
          <FormLabel htmlFor="name">Nome do {labels[type]}</FormLabel>
          <Controller
            control={control}
            name="name"
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                id="name"
                error={!!error}
                helperText={getErrorMessage(error)}
                placeholder="Digite o nome do item"
              />
            )}
          />
        </Stack>
      </FormControl>
      {type === ResourceTypes.Others && (
        <FormControl component="fieldset">
          <FormLabel component="legend" id="sale_type-label">
            Qual é o formato de pagamento do item?
          </FormLabel>
          <Controller
            control={control}
            name="sale_type"
            render={({ field }) => (
              <RadioGroup {...field} aria-labelledby="sale_type-label">
                <FormControlLabel
                  control={<Radio size="small" />}
                  label={<Typography>Compra única</Typography>}
                  value={PriceTypes.OneOff}
                />
                <FormControlLabel
                  control={<Radio size="small" />}
                  label={<Typography>Mensalidade</Typography>}
                  value={PriceTypes.Tuition}
                />
              </RadioGroup>
            )}
          />
        </FormControl>
      )}
      <FormControl error={!!errors.amount}>
        <Stack spacing={1}>
          <FormLabel htmlFor="amount" id="amount-label">
            {saleType === PriceTypes.Tuition
              ? 'Valor da mensalidade'
              : 'Custo total do item'}{' '}
            (R$)
          </FormLabel>
          <Controller
            control={control}
            name="amount"
            aria-labelledby="amount-label"
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                id="amount"
                error={!!error}
                helperText={getErrorMessage(error)}
                slotProps={{
                  input: {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    inputComponent: MoneyInput as any,
                    startAdornment: (
                      <InputAdornment disableTypography position="start">
                        R$
                      </InputAdornment>
                    ),
                  },
                }}
              />
            )}
          />
        </Stack>
      </FormControl>
      <Stack spacing={1}>
        <FormLabel htmlFor="link">
          Link do item
          <Box
            component="span"
            sx={{ pl: 1, fontWeight: 400, fontSize: '12px' }}
          >
            opcional
          </Box>
        </FormLabel>
        <Controller
          control={control}
          name="link"
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              id="link"
              error={!!error}
              helperText={getErrorMessage(error)}
              placeholder="Ex.: www.link.com"
            />
          )}
        />
      </Stack>
      <Stack spacing={1}>
        <FormLabel htmlFor="request_reason">
          Descrição ou justificativa
          <Box
            component="span"
            sx={{ pl: 1, fontWeight: 400, fontSize: '12px' }}
          >
            opcional
          </Box>
        </FormLabel>
        <Controller
          control={control}
          name="request_reason"
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              id="request_reason"
              error={!!error}
              helperText={getErrorMessage(error)}
              multiline
              rows={4}
              placeholder="Descreva o motivo da compra para avaliação da sua empresa."
            />
          )}
        />
      </Stack>
    </>
  );
};
